/**
 * Error Logger Configuration
 * 
 * Centralized configuration for client error tracking.
 * Allows enabling/disabling error logging via admin settings.
 */

import { supabase } from '@/integrations/supabase/client';

// Local cache for the enabled state
let isErrorLoggingEnabled: boolean | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 60000; // 1 minute cache

/**
 * Check if error logging is currently enabled
 * Uses local cache to avoid constant database queries
 */
export async function isErrorTrackingEnabled(): Promise<boolean> {
  // Check cache first (1 minute TTL)
  const now = Date.now();
  if (isErrorLoggingEnabled !== null && (now - lastFetchTime) < CACHE_DURATION) {
    return isErrorLoggingEnabled;
  }

  try {
    // Fetch from database
    const { data, error } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'error_tracking_enabled')
      .maybeSingle();

    if (error) {
      console.warn('[ErrorLogger] Failed to fetch setting, defaulting to enabled:', error);
      isErrorLoggingEnabled = true;
    } else if (data) {
      isErrorLoggingEnabled = data.value === true || data.value === 'true';
    } else {
      // Setting doesn't exist yet, default to enabled so errors are captured
      isErrorLoggingEnabled = true;
    }

    lastFetchTime = now;
    return isErrorLoggingEnabled;
  } catch (e) {
    console.warn('[ErrorLogger] Exception checking setting:', e);
    return true;
  }
}

/**
 * Update the error tracking enabled state
 * (Admin only)
 */
export async function setErrorTrackingEnabled(enabled: boolean): Promise<void> {
  try {
    const { error } = await supabase
      .from('app_settings')
      .upsert({
        key: 'error_tracking_enabled',
        value: enabled,
      }, {
        onConflict: 'key'
      });

    if (error) throw error;

    // Update cache immediately
    isErrorLoggingEnabled = enabled;
    lastFetchTime = Date.now();

  } catch (e) {
    console.error('[ErrorLogger] Failed to update setting:', e);
    throw e;
  }
}


